/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from 'react'
import type React from 'react'
import type { LinkProps } from 'react-router-dom'

import type {
  FlexContainerProperties,
  ThemeUICSSObject
} from '../../theme-provider/types'
import type { AnchorProps } from '../_primitives/anchor/types'
import type { BoxProps } from '../_primitives/box/types'
import type { FlexProps } from '../_primitives/flex/types'
import type { TextProps } from '../_primitives/text/types'
import type { SizeProps } from '../_primitives/types'

export const LIST_THEME_KEY = 'lists'

export type ListContext = {
  variant?: string
  dense?: boolean
  alignItems?: FlexContainerProperties['alignItems']
}

export type ListProps = FlexProps<HTMLUListElement> & {
  /**
   * The content of the component.
   */
  children: React.ReactNode | (() => JSX.Element)
  /**
   * If `true`, compact vertical padding designed for keyboard and mouse input will be used for
   * the list and list items.
   * The prop is available to descendant components as the `dense` context.
   */
  dense?: boolean
  /**
   * If `true`, vertical padding will be removed from the list.
   */
  disablePadding?: boolean
  /**
   * The content of the subheader, normally `ListSubheader`.
   */
  subheader?: React.ReactNode
  /**
   * Is loading state
   */
  isLoading?: boolean
}

export type ListItemProps<T = HTMLLIElement> = FlexProps<T> & {
  /**
   * If `true`, the list item will be focused during the first mount.
   * Focus will also be triggered if the value changes from false to true.
   */
  autoFocus?: boolean
  /**
   * If `true`, the list item will be a button (using `Button`). Props intended
   * for `Button` can then be applied to `ListItem`. Theme variant provided on the element
   * will use `Button` variant instead of the variant from `list` or it's compound __themeKey.
   */
  button?: boolean
  /**
   * The content of the component. If a `ListItemSecondaryAction` is used it must
   * be the last child.
   */
  children: ReactNode
  /**
   * The container component used when a `ListItemSecondaryAction` is the last child.
   */
  ContainerComponent?: keyof JSX.IntrinsicElements | React.ComponentType<any>
  /**
   * Props applied to the container component if used.
   */
  ContainerProps?: FlexProps<HTMLDivElement>
  /**
   * If `true`, compact vertical padding designed for keyboard and mouse input will be used.
   */
  dense?: boolean
  /**
   * If `true`, the list item will be disabled.
   */
  disabled?: boolean
  /**
   * If `true`, the left and right padding is removed.
   */
  disableGutters?: boolean
  /**
   * If `true`, a 1px light border is added to the bottom of the list item.
   */
  divider?: boolean
  /**
   * Use to apply selected styling.
   */
  selected?: boolean
  /**
   * show hover state in the list through programmatically
   */
  active?: boolean
  /**
   * data object for list item
   */
  data?: object
  /**
   * route link
   */
  to?: LinkProps['to']
}

export type ListItemAvatarProps = BoxProps
export type ListItemIconProps = BoxProps

export type ListItemTextProps = Omit<BoxProps, 'size'> &
  Pick<AnchorProps, 'target' | 'rel' | 'href' | 'disabled'> & {
    /**
     * If `true`, the children won't be wrapped by a Text component.
     * This can be useful to render an alternative Typography variant by wrapping
     * the `children` (or `primary`) text, and optional `secondary` text
     * with the Text component.
     */
    disableTextWrap?: boolean
    /**
     * If `true`, the children will be indented.
     * This should be used if there is no left avatar or left icon.
     */
    inset?: boolean
    /**
     * The main content element.
     */
    primary?: ReactNode
    /**
     * These props will be forwarded to the primary typography component
     * (as long as disableTextWrap is not `true`).
     */
    primaryTextProps?: TextProps
    /**
     * The secondary content element.
     */
    secondary?: ReactNode
    /**
     * These props will be forwarded to the secondary typography component
     * (as long as disableTextWrap is not `true`).
     */
    secondaryTextProps?: TextProps
    /**
     *  @deprecated size of the control are derive from fontSize via em units within the component
     * this applies to icons / spinner that are part of the control also.
     */
    size?: SizeProps
  }

export type ListSubheaderProps = FlexProps<HTMLLIElement> & {
  /**
   * If `true`, the List Subheader will not have gutters.
   */
  disableGutters?: boolean
  /**
   * If `true`, the List Subheader will not stick to the top during scroll.
   */
  disableSticky?: boolean
  /**
   * If `true`, the List Subheader will be indented.
   */
  inset?: boolean
}

export type ListItemLinkProps = ListItemProps<HTMLDivElement> &
  Pick<ListItemTextProps, 'primary'> & {
    to?: LinkProps['to']
  }

export type ListVariantTheme = {
  list?: ThemeUICSSObject
  listItem?: ThemeUICSSObject
}

export type ListVariants = Record<string, ListVariantTheme | ThemeUICSSObject>
